import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getFileUrl } from '../../lib/api';
import { heroImgClass, heroWrapperClass, heroHeightClass } from '../../lib/heroFit';

export const HeroSlider = ({ slides = [] }) => {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    if (slides.length > 0) {
      setCurrent(prev => (prev + 1) % slides.length);
    }
  }, [slides.length]);

  const prevSlide = () => {
    if (slides.length > 0) {
      setCurrent(prev => (prev - 1 + slides.length) % slides.length);
    }
  };

  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [isPaused, nextSlide, slides.length]);

  if (slides.length === 0) {
    return (
      <div className="relative w-full h-[60vh] md:h-[80vh] bg-secondary flex items-center justify-center">
        <p className="text-muted-foreground font-heading">Slider görsel eklenmemiş</p>
      </div>
    );
  }

  const slide = slides[current];
  const imgSrc = getFileUrl(slide.image_path);
  const fit = slide.fit_mode;

  return (
    <div
      className={`relative w-full ${heroHeightClass(fit, 'h-[60vh]', 'md:h-[80vh]')} overflow-hidden bg-[#0f1419]`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="home-hero-carousel"
    >
      {/* Background Image - fit per slider setting (full image on mobile by default) */}
      <div className={heroWrapperClass(fit)}>
        <img
          src={imgSrc}
          alt={slide.title}
          className={`${heroImgClass(fit)} transition-opacity duration-700`}
          key={current}
        />
      </div>

      {/* Content - positioned at bottom with a subtle scrim only behind text */}
      {(slide.title || slide.subtitle || slide.link) && (
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="bg-gradient-to-t from-black/70 via-black/40 to-transparent pt-20 pb-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white mb-2 drop-shadow-lg">
                {slide.title}
              </h1>
              {slide.subtitle && (
                <p className="text-sm sm:text-base text-white/80 mb-4 drop-shadow">
                  {slide.subtitle}
                </p>
              )}
              {slide.link && (
                <Link
                  to={slide.link}
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] font-medium text-sm hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors duration-200"
                  data-testid="home-hero-primary-cta"
                >
                  {slide.button_text || 'Projeyi İncele'}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 text-white transition-colors"
            data-testid="home-hero-carousel-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 text-white transition-colors"
            data-testid="home-hero-carousel-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Indicators */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${
                  i === current ? 'bg-[hsl(var(--gold))]' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
